import type { ShippingOptionContentVariantKeysByFulfilmentDay } from '@/components/order/shippingOptions/ShippingOptionCard/types';
import {
  get12HourFormat,
  getContent,
  getFulfilmentDay,
} from '@/components/order/shippingOptions/ShippingOptionCard/utils';
import { calculateDiscountedPrice } from '@/components/order/utils';
import { FF_SHIPPING_OPTIONS_ALLOW_DISPLAY_OF_SAME_DAY_AFTERNOON_OPTION } from '@/constants/featureFlags';
import { Logger } from '@/utils/logger';
import { DELIVERY_OPTION, DELIVERY_OPTIONS, getAdditionalTemporalInfo } from '@montugroup/circuit-shared';
import type { ArrayElement } from '@montugroup/circuit-shared/utils/typscript-helpers';
import { Typography } from '@montugroup/design-system';
import WbSunnyOutlinedIcon from '@mui/icons-material/WbSunnyOutlined';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import useFeatureFlags from '../useFeatureFlags';
import type { DeliveryOptions } from './types';

const logger = new Logger('useSameDayAfternoonDeliveryOption');

// TODO: CONTENT_VARIANTS to be sourced from LaunchDarkly
const CONTENT_VARIANTS: ShippingOptionContentVariantKeysByFulfilmentDay = {
  today: {
    title: 'A',
    description: 'A',
    icon: 'A',
  },
  tomorrow: {
    title: 'A',
    description: 'A',
    icon: 'A',
  },
};

const CONTENT = {
  today: {
    icons: { A: <WbSunnyOutlinedIcon /> },
    titles: { A: DELIVERY_OPTIONS[DELIVERY_OPTION.SameDayAfternoon].displayName },
    descriptions: {
      A: ({ cutoffTime, deliveryWindow }: { cutoffTime: string; deliveryWindow: { start: string; end: string } }) => {
        return (
          <Typography>
            Order before {cutoffTime}, arrive {deliveryWindow.start}-{deliveryWindow.end} today.
          </Typography>
        );
      },
    },
  },
  tomorrow: {
    icons: { A: <WbSunnyOutlinedIcon /> },
    titles: { A: DELIVERY_OPTIONS[DELIVERY_OPTION.SameDayAfternoon].displayNameAfterCutoff },
    descriptions: {
      A: ({ deliveryWindow }: { cutoffTime: string; deliveryWindow: { start: string; end: string } }) => {
        return (
          <Typography>
            Arrive {deliveryWindow.start}-{deliveryWindow.end} tomorrow.
          </Typography>
        );
      },
    },
  },
} as const;

const getSettings = (option: ArrayElement<DeliveryOptions['SameDayAfternoon']> | undefined, localDate: string) => {
  if (!option) {
    return null;
  }

  const { cutoffTime, deliveryWindow, expectedFulfilmentDate, shippingPrice, discount } = option;

  const fulfilmentDay = getFulfilmentDay(expectedFulfilmentDate, localDate);
  if (!fulfilmentDay) {
    return null;
  }

  const cutoffTimeHourFormatted = get12HourFormat(cutoffTime);
  const deliveryWindowHourFormatted = {
    start: get12HourFormat(deliveryWindow.startTime),
    end: get12HourFormat(deliveryWindow.endTime),
  };
  const content = getContent<(typeof CONTENT)[keyof typeof CONTENT]>({
    content: CONTENT[fulfilmentDay],
    variants: CONTENT_VARIANTS[fulfilmentDay],
    descriptionArgs: {
      cutoffTime: cutoffTimeHourFormatted,
      deliveryWindow: deliveryWindowHourFormatted,
    },
  });

  const discountedPrice = calculateDiscountedPrice({
    price: shippingPrice,
    discountPercent: discount?.percentage,
  });

  const extraDetails = {
    estimatedArrivalTime: `${deliveryWindowHourFormatted.start}-${deliveryWindowHourFormatted.end}`,
    discountPercentage: discount?.percentage,
    discountedPrice,
  };

  return {
    ...option,
    ...content,
    ...extraDetails,
  };
};

export default function useSameDayAfternoonDeliveryOption(props: DeliveryOptions['SameDayAfternoon']) {
  const { flags } = useFeatureFlags();

  // Read on every render, so the memo picks up local midnight passing on the next render rather than waiting for the
  // delivery options to be refetched
  const localDate = DateTime.now().toISODate();
  const settings = useMemo(() => getSettings(props[0], localDate), [props, localDate]);
  const shouldShow = !!settings && !!flags[FF_SHIPPING_OPTIONS_ALLOW_DISPLAY_OF_SAME_DAY_AFTERNOON_OPTION];

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { icon, description, ...loggableSettings } = settings ?? {};

    const additionalTemporalLogs = {
      browserTime: {
        ...getAdditionalTemporalInfo(DateTime.now().toISO()),
      },
      cutoffTime: {
        ...getAdditionalTemporalInfo(settings?.cutoffTime),
      },
      validBefore: {
        ...getAdditionalTemporalInfo(settings?.validBefore),
      },
      deliveryWindowStartTime: {
        ...getAdditionalTemporalInfo(settings?.deliveryWindow.startTime),
      },
      deliveryWindowEndTime: {
        ...getAdditionalTemporalInfo(settings?.deliveryWindow.endTime),
      },
    };

    const additionalDeliveryOptionSpecificLogs = settings
      ? {
          'flags[FF_SHIPPING_OPTIONS_ALLOW_DISPLAY_OF_SAME_DAY_AFTERNOON_OPTION]':
            flags[FF_SHIPPING_OPTIONS_ALLOW_DISPLAY_OF_SAME_DAY_AFTERNOON_OPTION],
        }
      : null;

    logger.info('settings', {
      ...loggableSettings,
      shouldShow,
      additionalTemporalLogs,
      additionalDeliveryOptionSpecificLogs,
      props,
    });

    // NOTE: The eslint rule doesn't correctly that we have added `flags[FF_SHIPPING_OPTIONS_ALLOW_DISPLAY_OF_SAME_DAY_AFTERNOON_OPTION]` instead of flags here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    settings,
    props,
    shouldShow,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    flags[FF_SHIPPING_OPTIONS_ALLOW_DISPLAY_OF_SAME_DAY_AFTERNOON_OPTION],
  ]);

  return shouldShow ? settings : null;
}
